/* https://github.com/pmiatkowski/angularNgrx/blob/master/src/app/state/effects/currency.effects.ts */
import { Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { concat, Observable, of } from 'rxjs';
import * as Services from '@shared/core/services';

import * as actions from '../actions';
import { ingredientsState } from './selectors/root';

@Injectable()
export class IngredientsEffects {
    public onMenuFlowSuccessRequest$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.MenuFlowsDetailsSuccessRequest),
            switchMap((action) => {
                /* Get regular images after menu flow downloads */
                const Pages: OLO.DTO.MenuFlowPageModel[] = action.payload.Pages;
                const productIds: number[] = Pages.reduce((acc, page) => acc.concat(page.Products.map((p) => p.ProductId)), []);

                return of(actions.IngredientsRequest({ locationNo: action.locationNo, productIds }));
            }),
        ),
    );

    public onRequestIngredient$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.IngredientsRequest),
            withLatestFrom(this._store.select(ingredientsState)),
            mergeMap(([{ locationNo, productIds }, ingredients]) => {
                // Filter out products that are already downloading or have succeeded
                const filteredProductIds = productIds.filter(
                    (productId) => !ingredients.some((ingredient) => ingredient.ProductId === productId && (ingredient.isDownloading || ingredient.hasSucceeded)),
                );

                if (filteredProductIds.length === 0) {
                    return of(actions.IngredientsSuccessRequest({ payload: [], locationNo, productIds: [] }));
                }

                return concat(
                    of(actions.MarkIngredientsAsDownloading({ locationNo, productIds: filteredProductIds })),
                    this._productsService.getIngredientsForLocationExtended(locationNo, ...filteredProductIds).pipe(
                        map((payload) => actions.IngredientsSuccessRequest({ payload, locationNo, productIds: filteredProductIds })),
                        catchError((ex) => of(actions.IngredientsErrorRequest({ locationNo, productIds: filteredProductIds, ex }))),
                    ),
                );
            }),
        ),
    );

    public onCartLoad$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.CartLoad),
            switchMap(({ cart }) => {
                const productIds = [...new Set(cart.itemsSimple.map((item) => item.ProductId))];

                return of(actions.IngredientsRequest({ locationNo: cart.locationNo, productIds }));
            }),
        ),
    );

    constructor(private _actions$: Actions, private _store: Store<OLO.State>, private _productsService: Services.ProductsService) {}
}
